// src/store/deals/deals.thunks.ts

import { createAsyncThunk, Dispatch } from "@reduxjs/toolkit";
import dealsService from "@/services/deals.service";
import {
    fetchDealsStart,
    fetchDealsSuccess,
    fetchDealsError,
    initStatusColumn,
    dealUpdated,
    updateDealRollback,
} from "./deals.slice";
import { RootState } from "@/store";
import { TDeal, TCreateDealPayload } from "@/models/deals.model";
import { getErrorFromBackend } from "@/utils/errors";

type FetchDealsByStatusArgs = {
    pipelineId: number;
    statusId: number;
};

const DEFAULT_PAGE_SIZE = 20;

export const fetchDealsByStatus = ({ pipelineId, statusId }: FetchDealsByStatusArgs) =>
    async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const { filters } = state.deals;

        const column = state.deals.byPipeline[pipelineId]?.byStatus[statusId];

        if (!column) dispatch(initStatusColumn({ pipelineId, statusId, pageSize: DEFAULT_PAGE_SIZE, }));

        const current = getState().deals.byPipeline[pipelineId].byStatus[statusId];

        if (current.loading) return;
        if (current.totalPages > 0 && current.page >= current.totalPages) return;

        const nextPage = current.page + 1;

        try {
            dispatch(fetchDealsStart({ pipelineId, statusId }));

            const response = await dealsService.getAll({
                pipelineId,
                status: statusId,
                page: nextPage,
                pageSize: current.pageSize,

                // global filters
                ...filters,
            });

            dispatch(fetchDealsSuccess({ pipelineId, statusId, response }));
        } catch (error: unknown) {
            dispatch(fetchDealsError({ pipelineId, statusId, error: getErrorFromBackend(error), }));
            throw error;
        }
    };


export const createDeal = createAsyncThunk<TDeal, TCreateDealPayload>("deals/createDeal", async (payload, { rejectWithValue }) => {
    try {
        return await dealsService.save(payload);
    } catch (err) {
        return rejectWithValue(err);
    }
});



export const updateDeal = createAsyncThunk<void,
    {
        dealId: number;
        pipelineId: number;
        updates: Partial<TDeal>;
    },
    { state: RootState }
>(
    "deals/updateDeal",
    async (
        { dealId, pipelineId, updates },
        { dispatch, getState, rejectWithValue }
    ) => {
        const state = getState();
        const deal = state.deals.entities[dealId];

        if (!deal) { return rejectWithValue("Deal not found"); }

        const previousState = Object.fromEntries(
            Object.keys(updates).map(key => [key, deal[key as keyof TDeal]])
        ) as Partial<TDeal>;

        dispatch(dealUpdated({ pipelineId, dealId, updates }));

        try {
            const updatedDeal = await dealsService.updateById(dealId, updates);
            dispatch(dealUpdated({ pipelineId, dealId, updates: updatedDeal }));
        } catch (err) {
            dispatch(
                updateDealRollback({
                    pipelineId,
                    dealId,
                    previousState,
                })
            );
            return rejectWithValue(err);
        }
    }
);

export const deleteDeal = createAsyncThunk<
    number,
    { dealId: number; pipelineId: number },
    { state: RootState }
>(
    "deals/deleteDeal",
    async ({ dealId }, { rejectWithValue }) => {
        try {
            await dealsService.deleteDeal(dealId);
            return dealId;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);
